import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { getAuthUrl } from "@/utils/redirectUtils";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Sparkles,
  Brain,
  Target,
  Rocket,
  BarChart3,
  LineChart,
  CheckCircle2,
  Globe,
  Lightbulb,
  Clock,
  Zap,
  Search,
  FileText,
  Mouse
} from "lucide-react";

const Features = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Features | AI-powered Google Ads Automation | Upfixe</title>
        <meta name="description" content="Explore Upfixe's AI-powered features for Google Ads: 2-minute setup, 5-minute campaign creation, AI ad copy, and actionable reporting." />
        <meta name="keywords" content="Google Ads features, AI ad automation, campaign creation, budget optimization, ad copy generation" />
        <meta property="og:title" content="Features | AI-powered Google Ads Automation | Upfixe" />
        <meta property="og:description" content="Explore Upfixe's AI-powered features for Google Ads: 2-minute setup, 5-minute campaign creation, AI ad copy, and actionable reporting." />
        <link rel="canonical" href="https://upfixe.com/features" />
      </Helmet>
      <Header />
      
      <main className="flex-grow">
        {/* Section 1: Connect & Setup */}
        <section className="pt-32 pb-20 px-4 bg-gradient-to-b from-background to-primary/5">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Built to Protect Your Daily Budget
              </h1>
              <p className="text-xl text-muted-foreground mb-12 max-w-3xl mx-auto">
                Stop paying for clicks that don't convert. Upfixe mechanically filters out keywords that exceed your Target CPA before you ever spend a dime.
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-muted">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="bg-primary/10 text-primary rounded-full p-3">
                      <Mouse className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">Single-Click Account Connection</h3>
                      <p className="text-muted-foreground">Connect Google Ads and MCC accounts instantly</p>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 border border-green-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Google Ads Account</span>
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">Connected in 3 seconds</div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-sm border border-muted">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="bg-primary/10 text-primary rounded-full p-3">
                      <FileText className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">Value Prop Document Creation</h3>
                      <p className="text-muted-foreground">AI-powered document serves as your campaign's foundation</p>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
                    <div className="text-sm font-medium mb-2">✨ AI-Generated Value Proposition</div>
                    <div className="text-xs text-muted-foreground">Editable • Brand-specific • Campaign-ready</div>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                <div className="bg-gradient-to-br from-primary/20 to-secondary/20 rounded-2xl p-8 text-center">
                  <Clock className="h-16 w-16 text-primary mx-auto mb-4" />
                  <h3 className="text-3xl font-bold mb-2">2 Minutes</h3>
                  <p className="text-muted-foreground">Average setup time from start to first campaign</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Campaign Creation */}
        <section className="py-20 px-4 bg-background">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Zero Wasted Spend Mechanics
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Set an ironclad Target CPA. Our AI extracts keywords directly from your landing page and mathematically eliminates any search term too expensive for your budget.
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <div className="bg-white rounded-xl p-6 shadow-lg border border-muted">
                  <div className="flex items-center gap-3 mb-6">
                    <Sparkles className="h-6 w-6 text-primary" />
                    <h3 className="text-xl font-bold">AI Campaign Builder</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="font-medium">Daily Budget</span>
                      <span className="text-primary font-bold">$50</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="font-medium">Target CPA</span>
                      <span className="text-primary font-bold">$25</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="font-medium">Seed Keyword</span>
                      <span className="text-primary font-bold">"running shoes"</span>
                    </div>
                  </div>
                  
                  <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Brain className="h-4 w-4 text-primary" />
                      <span className="text-sm font-medium">AI Processing...</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Generated 147 keywords • Created 12 ad groups • Built 36 ads
                    </div>
                  </div>
                </div>
                
                <div className="absolute -bottom-4 -right-4 bg-white rounded-lg p-4 shadow-lg border border-muted">
                  <Globe className="h-8 w-8 text-primary mb-2" />
                  <div className="text-sm font-medium">Multi-Language & Geo</div>
                  <div className="text-xs text-muted-foreground">15 countries • 8 languages</div>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="bg-primary/10 text-primary rounded-full p-3 flex-shrink-0">
                    <Search className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Google API Keyword Integration</h3>
                    <p className="text-muted-foreground">
                      Keywords pulled directly from Google's Keyword Planner API, ensuring relevance and search volume accuracy.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="bg-primary/10 text-primary rounded-full p-3 flex-shrink-0">
                    <Target className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Thematic Ad Group Organization</h3>
                    <p className="text-muted-foreground">
                      AI automatically groups related keywords into logical ad groups for better Quality Scores and relevance.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="bg-primary/10 text-primary rounded-full p-3 flex-shrink-0">
                    <Globe className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Multi-Language & Geo Targeting</h3>
                    <p className="text-muted-foreground">
                      Target multiple countries and languages simultaneously with intelligent localization.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: Automation & Optimization */}
        <section className="py-20 px-4 bg-muted/30">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Your AI Campaign Manager
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                AI trained on your landing page and value proposition creates perfect ads each time. Review and post directly to Google with a single click.
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="bg-primary/10 text-primary rounded-full p-3 flex-shrink-0">
                    <Brain className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">AI-Powered Ad Copy Generation</h3>
                    <p className="text-muted-foreground">
                      Every ad is crafted using your brand's unique value proposition and landing page content for maximum relevance.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="bg-primary/10 text-primary rounded-full p-3 flex-shrink-0">
                    <Lightbulb className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Perfect Ads Every Time</h3>
                    <p className="text-muted-foreground">
                      AI learns from your brand guidelines, ensuring consistent messaging and tone across all campaigns.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="bg-primary/10 text-primary rounded-full p-3 flex-shrink-0">
                    <CheckCircle2 className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Publish With a Safety Net</h3>
                    <p className="text-muted-foreground">
                      Review your filtered campaign and publish directly to Google Ads, knowing your budget limits are hardcoded.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-8 border border-green-200">
                  <div className="text-center mb-6">
                    <Rocket className="h-16 w-16 text-primary mx-auto mb-4" />
                    <h3 className="text-2xl font-bold mb-2">Campaign Ready!</h3>
                    <p className="text-muted-foreground">Your campaign has been generated and is ready to launch</p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 mb-4">
                    <div className="text-sm font-medium mb-2">🎯 Premium Running Shoes Campaign</div>
                    <div className="text-xs text-muted-foreground mb-3">
                      147 keywords • 12 ad groups • 36 ads • Ready for review
                    </div>
                    <div className="flex gap-2">
                      <button className="bg-primary text-primary-foreground text-xs py-2 px-4 rounded-full flex-1 flex items-center justify-center gap-1">
                        <Rocket className="h-3 w-3" />
                        Launch Campaign
                      </button>
                      <button className="bg-muted text-foreground text-xs py-2 px-4 rounded-full">
                        Review First
                      </button>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 text-xs py-1 px-3 rounded-full">
                      <CheckCircle2 className="h-3 w-3" />
                      Connected to Google Ads
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: Reporting & Insights */}
        <section className="py-20 px-4 bg-background">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Know What's Working, and How to Fix It
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Reports aren't just metrics – they're easy-to-understand summaries with precise, suggested actions to improve your campaigns.
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <div className="bg-white rounded-xl p-6 shadow-lg border border-muted">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold">Campaign Performance</h3>
                    <BarChart3 className="h-6 w-6 text-primary" />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="text-2xl font-bold text-green-600">+12.4%</div>
                      <div className="text-sm text-muted-foreground">CTR vs Last Week</div>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="text-2xl font-bold text-blue-600">+8.7%</div>
                      <div className="text-sm text-muted-foreground">Conversions</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="text-2xl font-bold text-purple-600">-15.2%</div>
                      <div className="text-sm text-muted-foreground">CPA</div>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <div className="text-2xl font-bold text-orange-600">2.3x</div>
                      <div className="text-sm text-muted-foreground">ROAS</div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4 border border-yellow-200">
                    <div className="flex items-start gap-3">
                      <Lightbulb className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-orange-800 mb-1">💡 Suggestion</div>
                        <div className="text-sm text-orange-700">
                          Raise CPA on Ad Group A by 10% to capture more high-intent traffic
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="bg-primary/10 text-primary rounded-full p-3 flex-shrink-0">
                    <LineChart className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Week-on-Week & Month-on-Month</h3>
                    <p className="text-muted-foreground">
                      Track performance trends with clear visualizations showing exactly how your campaigns are improving over time.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="bg-primary/10 text-primary rounded-full p-3 flex-shrink-0">
                    <Lightbulb className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Actionable Recommendations</h3>
                    <p className="text-muted-foreground">
                      Every report includes specific, data-driven suggestions you can implement immediately to improve performance.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="bg-primary/10 text-primary rounded-full p-3 flex-shrink-0">
                    <FileText className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Easy-to-Understand Summaries</h3>
                    <p className="text-muted-foreground">
                      Complex data transformed into simple insights that anyone can understand and act on, regardless of technical expertise.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 5: All Features at a Glance */}
        <section className="py-20 px-4 bg-muted/30">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Everything You Need to Win with Google Ads
              </h2>
              <p className="text-xl text-muted-foreground">
                A complete platform designed to streamline your Google Ads workflow from start to finish.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center gap-4 bg-white rounded-lg p-6 shadow-sm border border-muted">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="font-medium">Simple Account Connection</span>
              </div>
              
              <div className="flex items-center gap-4 bg-white rounded-lg p-6 shadow-sm border border-muted">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="font-medium">5-Minute Campaign Creation</span>
              </div>
              
              <div className="flex items-center gap-4 bg-white rounded-lg p-6 shadow-sm border border-muted">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="font-medium">AI-Powered Ad Copy</span>
              </div>
              
              <div className="flex items-center gap-4 bg-white rounded-lg p-6 shadow-sm border border-muted">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="font-medium">Intelligent Keyword Grouping</span>
              </div>
              
              <div className="flex items-center gap-4 bg-white rounded-lg p-6 shadow-sm border border-muted">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="font-medium">Multi-Language & Geo Targeting</span>
              </div>
              
              <div className="flex items-center gap-4 bg-white rounded-lg p-6 shadow-sm border border-muted">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="font-medium">One-Click Posting to Google</span>
              </div>
              
              <div className="flex items-center gap-4 bg-white rounded-lg p-6 shadow-sm border border-muted">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="font-medium">Actionable Reporting & Insights</span>
              </div>

            </div>
          </div>
        </section>

        {/* Blog Links Section */}
        <section className="py-20 px-4 bg-blue-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Learn How to Maximize These Features</h2>
              <p className="text-lg text-muted-foreground">
                Discover best practices for budget optimization, CPA reduction, and AI-powered campaign management.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <a href="/blog/budget-optimization-guide" className="group bg-white p-6 rounded-lg hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600">Budget Optimization Guide</h3>
                <p className="text-sm text-muted-foreground mb-4">Master framework for maximizing ROI on every dollar spent across all campaigns.</p>
                <span className="text-sm font-semibold text-blue-600">Read Article →</span>
              </a>
              <a href="/blog/reduce-google-ads-cpa" className="group bg-white p-6 rounded-lg hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600">Reduce Google Ads CPA</h3>
                <p className="text-sm text-muted-foreground mb-4">7 proven strategies to cut your cost-per-acquisition in half.</p>
                <span className="text-sm font-semibold text-blue-600">Read Article →</span>
              </a>
              <a href="/blog/ai-vs-manual-google-ads" className="group bg-white p-6 rounded-lg hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600">AI vs Manual Ads</h3>
                <p className="text-sm text-muted-foreground mb-4">Compare costs and results: agencies vs AI platforms vs DIY approaches.</p>
                <span className="text-sm font-semibold text-blue-600">Read Article →</span>
              </a>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 px-4 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Ready to Stop Burning Your Ad Spend?
            </h2>
            <p className="text-xl text-muted-foreground mb-12 max-w-2xl mx-auto">
              Join businesses that have replaced guesswork with mathematical budget protection to dramatically increase their ROI.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="text-lg px-8" onClick={() => window.location.href = getAuthUrl()}>
                Start Free Account <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button variant="outline" size="lg" className="text-lg px-8" asChild>
                <a href="/pricing">View Pricing</a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default Features;